import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Pause, Trash2, Save, X } from "lucide-react";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import { SECTION_COLORS, SECTION_BORDER_COLORS } from "./sectionColors";

const numberInputStyle = {
  MozAppearance: "textfield",
  WebkitAppearance: "none",
  appearance: "textfield",
};

/**
 * Hook that manages all section editor state and logic.
 * Returns state object consumed by SectionEditorLyrics and SectionEditorPanel.
 */
export const useSectionEditor = ({
  active,
  song,
  beatWaveSurfer,
  initialSections,
  onSave,
  onCancel,
  isMobile = false,
  beatDuration = 0,
}) => {
  const [sections, setSections] = useState(initialSections || []);

  // Sync from initialSections when they change (e.g., config loaded from storage after mount)
  useEffect(() => {
    setSections(initialSections || []);
  }, [initialSections]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const animFrameRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const regionsMapRef = useRef(new Map());
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const lines = useMemo(
    () => (song?.content ? song.content.split("\n") : []),
    [song?.content],
  );

  // Initialize RegionsPlugin on the existing beat WaveSurfer
  useEffect(() => {
    if (!active || !beatWaveSurfer) return;

    const regions = beatWaveSurfer.registerPlugin(RegionsPlugin.create());
    regionsPluginRef.current = regions;
    regionsMapRef.current.clear();

    // Seed existing sections as regions immediately so they appear on re-open
    sectionsRef.current.forEach((section, idx) => {
      const color = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
      const region = regions.addRegion({
        id: section.id,
        start: section.audioStartSec,
        end: section.audioEndSec,
        color,
        drag: true,
        resize: true,
      });
      regionsMapRef.current.set(section.id, region);
    });

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFinish = () => setIsPlaying(false);
    beatWaveSurfer.on("play", onPlay);
    beatWaveSurfer.on("pause", onPause);
    beatWaveSurfer.on("finish", onFinish);

    regions.on("region-updated", (region) => {
      setSections((prev) => {
        const sorted = [...prev].sort(
          (a, b) => a.audioStartSec - b.audioStartSec,
        );
        const idx = sorted.findIndex((s) => s.id === region.id);
        if (idx < 0) return prev;

        // Clamp to neighbors so sections never overlap
        const minStart =
          idx > 0 ? sorted[idx - 1].audioEndSec : 0;
        const maxEnd =
          idx < sorted.length - 1
            ? sorted[idx + 1].audioStartSec
            : beatDuration || region.end;

        let clampedStart = Math.max(region.start, minStart);
        let clampedEnd = Math.min(region.end, maxEnd);

        // Ensure the region keeps a minimum width (0.1s)
        if (clampedEnd - clampedStart < 0.1) {
          clampedStart = Math.max(minStart, clampedEnd - 0.1);
        }

        // If clamped values differ from the region, update the visual region
        if (
          Math.abs(region.start - clampedStart) > 0.001 ||
          Math.abs(region.end - clampedEnd) > 0.001
        ) {
          region.setOptions({ start: clampedStart, end: clampedEnd });
        }

        return prev.map((s) =>
          s.id === region.id
            ? { ...s, audioStartSec: clampedStart, audioEndSec: clampedEnd }
            : s,
        );
      });
    });

    return () => {
      beatWaveSurfer.un("play", onPlay);
      beatWaveSurfer.un("pause", onPause);
      beatWaveSurfer.un("finish", onFinish);
      regions.clearRegions();
      regions.destroy();
      regionsPluginRef.current = null;
      regionsMapRef.current.clear();
    };
  }, [active, beatWaveSurfer]);

  // Track current line during playback using word-weighted interpolation
  useEffect(() => {
    if (!isPlaying || !beatWaveSurfer) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (!isPlaying) setCurrentLineIndex(-1);
      return;
    }

    const animate = () => {
      const currentSections = sectionsRef.current;
      if (!currentSections?.length) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      const currentTime = beatWaveSurfer.getCurrentTime();

      // Build word-weighted line timestamps from current sections
      const lineTimes = [];
      for (const section of currentSections) {
        const audioDur = section.audioEndSec - section.audioStartSec;
        const sectionLines = [];
        let totalWords = 0;
        for (let i = section.startLine - 1; i < section.endLine && i < lines.length; i++) {
          const words = lines[i].split(/\s+/).filter(Boolean).length;
          sectionLines.push({ index: i, words });
          totalWords += words;
        }
        let cumTime = section.audioStartSec;
        for (const line of sectionLines) {
          lineTimes.push({ index: line.index, time: cumTime });
          if (totalWords > 0) {
            cumTime += (line.words / totalWords) * audioDur;
          }
        }
      }
      lineTimes.sort((a, b) => a.time - b.time);

      // Reverse scan to find current line
      let found = -1;
      for (let i = lineTimes.length - 1; i >= 0; i--) {
        if (currentTime >= lineTimes[i].time) {
          found = lineTimes[i].index;
          break;
        }
      }
      setCurrentLineIndex(found);

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying, beatWaveSurfer, lines]);

  // Sync regions to sections state
  useEffect(() => {
    const regions = regionsPluginRef.current;
    if (!regions) return;

    const currentMap = regionsMapRef.current;
    const currentIds = new Set(sections.map((s) => s.id));

    for (const [id, region] of currentMap.entries()) {
      if (!currentIds.has(id)) {
        region.remove();
        currentMap.delete(id);
      }
    }

    sections.forEach((section, idx) => {
      const existing = currentMap.get(section.id);
      if (existing) {
        if (
          Math.abs(existing.start - section.audioStartSec) > 0.01 ||
          Math.abs(existing.end - section.audioEndSec) > 0.01
        ) {
          existing.setOptions({
            start: section.audioStartSec,
            end: section.audioEndSec,
          });
        }
      } else {
        const color = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
        const region = regions.addRegion({
          id: section.id,
          start: section.audioStartSec,
          end: section.audioEndSec,
          color,
          drag: true,
          resize: true,
        });
        currentMap.set(section.id, region);
      }
    });
  }, [sections]);

  const togglePlayPause = useCallback(() => {
    if (!beatWaveSurfer) return;
    if (isPlaying) {
      beatWaveSurfer.pause();
    } else {
      beatWaveSurfer.play();
    }
  }, [beatWaveSurfer, isPlaying]);

  const handleLineClick = useCallback(
    (lineNum) => {
      setSections((prev) => {
        const existingIdx = prev.findIndex((s) => s.startLine === lineNum);
        if (existingIdx >= 0) {
          if (existingIdx === 0) return prev;
          const merged = [...prev];
          merged[existingIdx - 1] = {
            ...merged[existingIdx - 1],
            endLine: merged[existingIdx].endLine,
            audioEndSec: merged[existingIdx].audioEndSec,
          };
          merged.splice(existingIdx, 1);
          return merged;
        }

        const containingIdx = prev.findIndex(
          (s) => lineNum >= s.startLine && lineNum <= s.endLine,
        );

        if (containingIdx >= 0) {
          const old = prev[containingIdx];
          if (lineNum === old.startLine) return prev;

          // Trim old section's line range but keep its audio/WPM intact
          const trimmed = { ...old, endLine: lineNum - 1 };

          // New section starts after the old one's audio range
          const nextSection = prev[containingIdx + 1];
          const colorIdx = prev.length % SECTION_COLORS.length;
          const newSection = {
            id: `section-${Date.now()}`,
            name: `Section ${prev.length + 1}`,
            startLine: lineNum,
            endLine: old.endLine,
            audioStartSec: old.audioEndSec,
            audioEndSec: nextSection ? nextSection.audioStartSec : (beatDuration || 0),
            wpm: 120,
            color: SECTION_COLORS[colorIdx],
          };

          const result = [...prev];
          result.splice(containingIdx, 1, trimmed, newSection);
          return result;
        }

        if (prev.length === 0) {
          return [{
            id: `section-${Date.now()}`,
            name: "Section 1",
            startLine: 1,
            endLine: lines.length,
            audioStartSec: 0,
            audioEndSec: beatDuration || 0,
            wpm: 120,
            color: SECTION_COLORS[0],
          }];
        }

        // Line is outside any section — create a new one in the gap
        const sorted = [...prev].sort((a, b) => a.startLine - b.startLine);
        const nextSection = sorted.find((s) => s.startLine > lineNum);
        const prevSection = [...sorted].reverse().find((s) => s.endLine < lineNum);
        const endLine = nextSection ? nextSection.startLine - 1 : lines.length;
        const audioStart = prevSection ? prevSection.audioEndSec : 0;
        const audioEnd = nextSection ? nextSection.audioStartSec : (beatDuration || 0);
        const colorIdx = prev.length % SECTION_COLORS.length;

        const newSection = {
          id: `section-${Date.now()}`,
          name: `Section ${prev.length + 1}`,
          startLine: lineNum,
          endLine,
          audioStartSec: audioStart,
          audioEndSec: audioEnd,
          wpm: 120,
          color: SECTION_COLORS[colorIdx],
        };

        return [...prev, newSection].sort((a, b) => a.startLine - b.startLine);
      });
    },
    [lines.length, beatDuration],
  );

  const updateSection = useCallback((sectionId, updates) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
    );
  }, []);

  const deleteSection = useCallback((sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  }, []);

  const autoDetectSectionWPM = useCallback(
    (section) => {
      const audioDur = section.audioEndSec - section.audioStartSec;
      if (audioDur <= 0) return;
      let wordCount = 0;
      for (let i = section.startLine - 1; i < section.endLine && i < lines.length; i++) {
        wordCount += lines[i].split(/\s+/).filter(Boolean).length;
      }
      if (wordCount === 0) return;
      const wpm = Math.round((wordCount / audioDur) * 60);
      updateSection(section.id, { wpm: Math.max(30, Math.min(300, wpm)) });
    },
    [lines, updateSection],
  );

  const handleCreateInitialSection = useCallback(() => {
    setSections([{
      id: `section-${Date.now()}`,
      name: "Section 1",
      startLine: 1,
      endLine: lines.length,
      audioStartSec: 0,
      audioEndSec: beatDuration || 0,
      wpm: 120,
      color: SECTION_COLORS[0],
    }]);
  }, [lines.length, beatDuration]);

  const getLineSection = useCallback(
    (lineNum) => {
      const idx = sections.findIndex(
        (s) => lineNum >= s.startLine && lineNum <= s.endLine,
      );
      return idx >= 0 ? { section: sections[idx], index: idx } : null;
    },
    [sections],
  );

  const handleSave = useCallback(() => {
    if (beatWaveSurfer) {
      try { beatWaveSurfer.pause(); } catch (e) { /* ignore */ }
    }
    onSave(sections);
  }, [sections, onSave, beatWaveSurfer]);

  const handleCancel = useCallback(() => {
    if (beatWaveSurfer) {
      try { beatWaveSurfer.pause(); } catch (e) { /* ignore */ }
    }
    onCancel();
  }, [onCancel, beatWaveSurfer]);

  return {
    sections,
    lines,
    isPlaying,
    currentLineIndex,
    isMobile,
    beatDuration,
    togglePlayPause,
    handleLineClick,
    updateSection,
    deleteSection,
    autoDetectSectionWPM,
    handleCreateInitialSection,
    getLineSection,
    handleSave,
    handleCancel,
  };
};

/**
 * Lyrics panel — clickable lines with section coloring.
 * Rendered as the "main content area" of the teleprompter.
 */
export const SectionEditorLyrics = ({ editor }) => {
  const {
    lines,
    sections,
    currentLineIndex,
    isPlaying,
    handleLineClick,
    getLineSection,
    handleCreateInitialSection,
  } = editor;

  const containerRef = useRef(null);
  const lineRefs = useRef([]);

  // Auto-scroll to current line during playback
  useEffect(() => {
    if (currentLineIndex < 0 || !lineRefs.current[currentLineIndex] || !containerRef.current) return;
    const lineEl = lineRefs.current[currentLineIndex];
    const container = containerRef.current;
    const targetScroll = lineEl.offsetTop - container.clientHeight / 2 + lineEl.clientHeight / 2;
    container.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: "smooth",
    });
  }, [currentLineIndex]);

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto px-4 md:px-8 py-4"
      style={{ flex: "1 1 0%", minHeight: 0 }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-forest-400 uppercase tracking-wide">
          Click a line to mark a section boundary
        </span>
        {sections.length === 0 && (
          <button
            onClick={handleCreateInitialSection}
            className="text-xs px-2 py-1 rounded bg-forest-700 hover:bg-forest-600 text-gold-500 border border-forest-600 transition-colors"
          >
            + Create Section
          </button>
        )}
      </div>

      {lines.map((line, i) => {
        const lineNum = i + 1;
        const lineSec = getLineSection(lineNum);
        const colorIdx = lineSec ? lineSec.index % SECTION_BORDER_COLORS.length : -1;
        const borderColor = colorIdx >= 0 ? SECTION_BORDER_COLORS[colorIdx] : "transparent";
        const isBoundary = sections.some((s) => s.startLine === lineNum);
        const isCurrent = i === currentLineIndex;
        const isPast = currentLineIndex >= 0 && i < currentLineIndex;

        return (
          <div
            key={i}
            ref={(el) => (lineRefs.current[i] = el)}
            onClick={() => handleLineClick(lineNum)}
            className={`flex items-start gap-2 py-1 text-sm cursor-pointer rounded-r px-2 transition-colors hover:bg-forest-800/50 ${
              isBoundary ? "mt-1" : ""
            }`}
            style={{
              borderLeft: `3px solid ${borderColor}`,
              backgroundColor: isCurrent
                ? "rgba(196, 164, 78, 0.15)"
                : isBoundary
                  ? "rgba(196, 164, 78, 0.05)"
                  : undefined,
            }}
          >
            <span
              className={`text-xs w-6 text-right flex-shrink-0 font-mono pt-0.5 ${
                isCurrent ? "text-gold-500" : "text-forest-500"
              }`}
            >
              {lineNum}
            </span>
            <span
              className={`flex-1 transition-colors duration-150 ${
                isCurrent
                  ? "text-gold-500 font-semibold"
                  : isPast
                    ? "text-forest-500"
                    : "text-forest-200"
              }`}
            >
              {line || "\u00A0"}
            </span>
            {isBoundary && (
              <span className="text-xs text-gold-500/60 flex-shrink-0 font-mono">
                {sections.find((s) => s.startLine === lineNum)?.name}
              </span>
            )}
          </div>
        );
      })}
      <div style={{ height: "40px" }} />
    </div>
  );
};

/**
 * Sections panel — per-section controls, save/cancel.
 * Rendered as the "controls area" of the teleprompter.
 */
export const SectionEditorPanel = ({ editor }) => {
  const {
    sections,
    isPlaying,
    isMobile,
    beatDuration,
    togglePlayPause,
    updateSection,
    deleteSection,
    autoDetectSectionWPM,
    handleSave,
    handleCancel,
  } = editor;

  return (
    <div
      className="bg-forest-900/90 backdrop-blur-sm border-t border-forest-700/50 px-4 py-3 overflow-y-auto"
      style={{ maxHeight: isMobile ? "45vh" : "280px", flexShrink: 0 }}
    >
      {/* Play/Pause for beat preview */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={togglePlayPause}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
        >
          {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {isPlaying ? "Pause Beat" : "Play Beat"}
        </button>
        <span className="text-xs text-forest-500">
          {sections.length} section{sections.length !== 1 ? "s" : ""}
        </span>
      </div>

      {sections.length === 0 && (
        <p className="text-xs text-forest-500 py-2">
          Click lyric lines to mark section boundaries, or use the "Create
          Section" button above.
        </p>
      )}

      {sections.map((section, idx) => (
        <div
          key={section.id}
          className="mb-2 p-2 rounded border bg-forest-800/50"
          style={{ borderColor: SECTION_BORDER_COLORS[idx % SECTION_BORDER_COLORS.length] }}
        >
          <div className="flex items-center gap-2 mb-1">
            <input
              type="text"
              value={section.name}
              onChange={(e) => updateSection(section.id, { name: e.target.value })}
              className="flex-1 text-sm px-2 py-0.5 bg-forest-900 border border-forest-600 rounded text-forest-200 focus:outline-none focus:border-gold-500"
              onKeyDown={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => deleteSection(section.id)}
              className="p-1 rounded text-ember-400 hover:bg-ember-900/30 transition-colors"
              title="Delete section"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-forest-400">
              L{section.startLine}–{section.endLine}
            </span>
            <span className="text-xs text-forest-600">|</span>

            {/* Speed stepper */}
            <div className="flex items-center" style={{ height: "24px" }}>
              <button
                onClick={() => updateSection(section.id, { wpm: Math.max(30, (section.wpm || 120) - 10) })}
                className="flex items-center justify-center rounded-l bg-forest-700 hover:bg-forest-600 text-forest-300 border border-forest-600 text-xs font-bold select-none"
                style={{ width: "24px", height: "24px" }}
              >
                -
              </button>
              <input
                type="number"
                value={section.wpm || 120}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v) && v >= 30 && v <= 300) updateSection(section.id, { wpm: v });
                }}
                className="text-center text-xs font-mono bg-forest-800 text-forest-200 border-y border-forest-600 focus:outline-none focus:border-gold-500"
                style={{ width: "52px", height: "24px", ...numberInputStyle }}
                min={30}
                max={300}
                step={10}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => updateSection(section.id, { wpm: Math.min(300, (section.wpm || 120) + 10) })}
                className="flex items-center justify-center rounded-r bg-forest-700 hover:bg-forest-600 text-forest-300 border border-forest-600 text-xs font-bold select-none"
                style={{ width: "24px", height: "24px" }}
              >
                +
              </button>
            </div>

            {/* Per-section auto WPM */}
            <button
              onClick={() => autoDetectSectionWPM(section)}
              disabled={!beatDuration || section.audioEndSec - section.audioStartSec <= 0}
              className={`text-xs px-1.5 py-0.5 rounded border transition-colors font-bold select-none ${
                !beatDuration || section.audioEndSec - section.audioStartSec <= 0
                  ? "bg-forest-800 text-forest-600 border-forest-700 cursor-not-allowed"
                  : "bg-forest-700 hover:bg-forest-600 text-gold-500 border-forest-600"
              }`}
              title="Auto-detect WPM from section audio duration"
            >
              WPM
            </button>

            {beatDuration > 0 && (
              <span className="text-xs text-forest-500 font-mono ml-auto">
                {section.audioStartSec?.toFixed(1)}s–{section.audioEndSec?.toFixed(1)}s
              </span>
            )}
          </div>
        </div>
      ))}

      {/* Save / Cancel */}
      <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-forest-700/50">
        <button
          onClick={handleCancel}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-300"
        >
          <X className="w-3.5 h-3.5" />
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500 border border-gold-500/30"
        >
          <Save className="w-3.5 h-3.5" />
          Save Sections
        </button>
      </div>

      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
};
